import type { Practitioner } from '@medplum/fhirtypes';
import { useMedplum, useMedplumProfile } from '@medplum/react-hooks';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { hasOnboardingAttestation } from '@unify/fhir-model/resources/practitioner';

export function useOnboardingPractitioner(): Practitioner | undefined {
  const medplum = useMedplum();
  const sessionProfile = useMedplumProfile() as Practitioner | undefined;
  const location = useLocation();
  const [practitioner, setPractitioner] = useState<Practitioner | undefined>(() =>
    sessionProfile?.resourceType === 'Practitioner' ? sessionProfile : undefined,
  );

  useEffect(() => {
    if (sessionProfile?.resourceType === 'Practitioner') {
      setPractitioner(sessionProfile);
    }
  }, [sessionProfile]);

  const isCompleted = practitioner ? hasOnboardingAttestation(practitioner) : false;

  useEffect(() => {
    if (!sessionProfile?.id || sessionProfile.resourceType !== 'Practitioner') {
      return;
    }

    // Short-circuit if the practitioner is already fully onboarded.
    // This prevents firing a network request on every route change for active providers.
    if (isCompleted) {
      return;
    }

    let cancelled = false;

    medplum
      .readResource('Practitioner', sessionProfile.id)
      .then((freshPractitioner) => {
        if (!cancelled) {
          setPractitioner(freshPractitioner);
        }
      })
      .catch(() => {
        // Keep the cached session profile if the refresh fails.
      });

    return () => {
      cancelled = true;
    };
  }, [location.pathname, medplum, sessionProfile?.id, sessionProfile?.resourceType, isCompleted]);

  return practitioner;
}
